/**
 * HowToPage - the hidden /how-to guide for first-time users.
 *
 * Not linked from the navigation on purpose: it's handed out in onboarding
 * emails and by managers ("have a look at /how-to"). It works signed in or
 * signed out — signed in, it sits inside the normal app chrome and opens on the
 * guide for the viewer's own role; signed out, it renders on its own with a
 * Sign in link, so a new hire can read it before their first login.
 *
 * Content lives in content/howToGuide.ts (kept in step with USER-GUIDE.md —
 * see CLAUDE.md). This page only arranges it:
 *
 *   1. "Who are you?" picker  — one tab per audience (field crew, reviewer,
 *                               manager, admin, super admin). ?for=<id> in the
 *                               URL picks one, so a link can be role-specific.
 *   2. Audience intro         — who this is, plus a "Your first day" checklist.
 *   3. Search                 — filters every topic by its words.
 *   4. Topics, by group       — collapsible HowToTopicCards; a topic with a
 *                               walkthrough shows its HowToVideo above the steps.
 *   5. Glossary               — what the statuses and words mean.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { PageContainer } from '../../components/templates/PageContainer';
import { TabBar } from '../../components/molecules/TabBar';
import { SearchBar } from '../../components/molecules/SearchBar';
import { SectionCard } from '../../components/molecules/SectionCard';
import { HowToTopicCard, renderGuideText } from '../../components/molecules/HowToTopicCard';
import { HowToVideo } from '../../components/molecules/HowToVideo';
import { BrandWordmark } from '../../components/atoms';
import {
	HOW_TO_AUDIENCES,
	HOW_TO_GLOSSARY,
	HOW_TO_GROUPS,
	audienceForRoles,
	topicsForAudience,
	topicMatches,
} from '../../content/howToGuide';
import type { HowToAudienceId } from '../../content/howToGuide';

/** True when `value` names one of the audiences. */
const isAudienceId = (value: string | null): value is HowToAudienceId =>
	HOW_TO_AUDIENCES.some((a) => a.id === value);

const HowToPage: React.FC = () => {
	const { user, isAuthenticated } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();
	const [query, setQuery] = useState('');

	/* Audience: the URL wins (shareable links), then the viewer's own role,
	   then the field crew — the largest group and the most common first-timer. */
	const fromUrl = searchParams.get('for');
	const audienceId: HowToAudienceId = isAudienceId(fromUrl)
		? fromUrl
		: audienceForRoles(user?.roles ?? []);
	const audience = HOW_TO_AUDIENCES.find((a) => a.id === audienceId) ?? HOW_TO_AUDIENCES[0];

	/** Switch audience and remember it in the URL. */
	const chooseAudience = (id: string) => {
		const next = new URLSearchParams(searchParams);
		next.set('for', id);
		setSearchParams(next, { replace: true });
	};

	/* Topics for this audience, narrowed by the search box. */
	const topics = useMemo(
		() => topicsForAudience(audience.id).filter((t) => topicMatches(t, query)),
		[audience.id, query],
	);

	/* Deep links (/how-to#start-a-job): open and scroll to that topic once
	   the cards are on the page. */
	useEffect(() => {
		const hash = window.location.hash.slice(1);
		if (!hash) return;
		const el = document.getElementById(hash);
		if (el instanceof HTMLDetailsElement) el.open = true;
		el?.scrollIntoView({ block: 'start' });
	}, []);

	return (
		<PageContainer block="how-to-page" dataRef="page-how-to-page" width="narrow">
			{/* Signed-out readers get the brand and a way in; signed-in readers
			    already have both in the app chrome. */}
			{!isAuthenticated && (
				<div className="how-to-page__public-bar flex items-center justify-between gap-12px mb-24px">
					<BrandWordmark />
					<Link to="/login" className="inline-flex items-center min-h-44px text-14px font-sans font-semibold text-brand-primary hover:underline">Sign in</Link>
				</div>
			)}

			{/* --- Hero --- */}
			<header className="how-to-page__hero mb-24px">
				<h1 className="mb-8px text-28px font-heading font-bold text-brand-heading">How to use GBrands</h1>
				<p className="text-16px font-sans text-brand-text-muted leading-relaxed">
					A plain-English guide to everything in the app. Pick who you are below and we&apos;ll show you the parts that matter to you — start with <strong className="text-brand-text">Your first day</strong>.
				</p>
			</header>

			{/* --- Who are you? --- */}
			<section className="how-to-page__audience mb-24px" aria-labelledby="how-to-audience-heading">
				<h2 id="how-to-audience-heading" className="mb-8px text-14px font-sans font-semibold uppercase tracking-wider text-brand-text-muted">I am…</h2>
				<TabBar tabs={HOW_TO_AUDIENCES.map((a) => ({ key: a.id, label: a.label }))} activeKey={audience.id} onChange={chooseAudience} wrap />
			</section>

			{/* --- Audience intro + first day --- */}
			<SectionCard title={audience.headline} className="how-to-page__intro mb-24px" dataRef="section-how-to-intro">
				<p className="mb-8px text-15px font-sans text-brand-text leading-relaxed">{renderGuideText(audience.intro)}</p>
				<p className="mb-16px text-13px font-sans text-brand-text-muted">{`Covers: ${audience.covers}`}</p>
				<h3 className="mb-8px text-16px font-sans font-semibold text-brand-text">Your first day</h3>
				<ol className="flex flex-col gap-8px list-decimal pl-20px text-15px font-sans text-brand-text leading-relaxed">
					{audience.firstDay.map((step, i) => <li key={i}>{renderGuideText(step)}</li>)}
				</ol>
			</SectionCard>

			{/* --- Search --- */}
			<div className="how-to-page__search mb-16px">
				<SearchBar value={query} onChange={setQuery} placeholder="Search the guide — e.g. photos, hours, sign off" />
			</div>

			{/* --- Topics, grouped --- */}
			{topics.length === 0 ? (
				<p className="py-24px text-15px font-sans text-brand-text-muted text-center">Nothing in this guide matches that. Try a different word, or pick another role above.</p>
			) : (
				HOW_TO_GROUPS.map((group) => {
					const inGroup = topics.filter((t) => t.group === group.id);
					if (inGroup.length === 0) return null;
					return (
						<section key={group.id} className="how-to-page__group mb-24px" aria-labelledby={`how-to-group-${group.id}`}>
							<h2 id={`how-to-group-${group.id}`} className="mb-4px text-20px font-heading font-bold text-brand-heading">{group.title}</h2>
							<p className="mb-12px text-14px font-sans text-brand-text-muted">{group.description}</p>
							<div className="flex flex-col gap-8px">
								{inGroup.map((topic) => (
									<HowToTopicCard key={topic.id} id={topic.id} title={topic.title} summary={topic.summary} steps={topic.steps} tips={topic.tips} media={topic.video ? <HowToVideo video={topic.video} title={topic.title} /> : undefined} defaultOpen={query.trim().length > 0} />
								))}
							</div>
						</section>
					);
				})
			)}

			{/* --- Glossary --- */}
			<SectionCard title="What the words mean" className="how-to-page__glossary mb-24px" dataRef="section-how-to-glossary">
				<dl className="grid grid-cols-1 tablet:grid-cols-[180px_1fr] gap-x-16px gap-y-8px text-15px font-sans">
					{HOW_TO_GLOSSARY.map((entry) => (
						<React.Fragment key={entry.term}>
							<dt className="font-semibold text-brand-text">{entry.term}</dt>
							<dd className="mb-8px tablet:mb-0 text-brand-text-muted">{renderGuideText(entry.meaning)}</dd>
						</React.Fragment>
					))}
				</dl>
			</SectionCard>

			<p className="text-14px font-sans text-brand-text-muted text-center">
				Still stuck? Ask your manager, or the office team who set up your account.
			</p>
		</PageContainer>
	);
};

export default HowToPage;
